import manifestJson from '@/content/manifest.json';
import citationsJson from '@/content/citations.json';

export type CitationType =
  | 'paper'
  | 'blog'
  | 'vendor-doc'
  | 'regulation'
  | 'standard'
  | 'book'
  | 'course'
  | 'tool-docs'
  | 'case-study'
  | 'wikipedia';

export interface Citation {
  key: string;
  type: CitationType;
  title: string;
  authors?: string;
  year?: number;
  venue?: string;
  url: string;
  arxivId?: string;
  bibtexId?: string;
  takeaway?: string;
  verified: string;
}

export interface Heading {
  depth: number;
  text: string;
  slug: string;
  id: string;
}

export type SectionKind =
  | 'learn'
  | 'techniques'
  | 'tools'
  | 'papers'
  | 'cookbook'
  | 'task-types'
  | 'start';

export interface TechniqueQuickRef {
  whenToUse?: string;
  whenNotToUse?: string;
  keyPitfall?: string;
  canonicalCitation?: string;
}

export interface Chapter {
  slug: string;
  title: string;
  description: string;
  personas: string[];
  citations: string[];
  prerequisites: string[];
  relatedPages: string[];
  published: string | null;
  updated: string;
  verified: string;
  owner: string;
  draft: boolean;
  order: number;
  readingMinutes: number;
  wordCount: number;
  headings: Heading[];
  contentFile: string;
  sourcePath: string;
  hasMermaid: boolean;
  hasCytoscape: boolean;
  hasWardley: boolean;
  quickRef?: TechniqueQuickRef;
}

export interface Section {
  slug: string;
  title: string;
  tagline: string;
  kind: SectionKind;
  order: number;
  chapters: Chapter[];
  overviewContentFile?: string;
  overviewHasMermaid?: boolean;
  overviewHasCytoscape?: boolean;
  overviewHasWardley?: boolean;
}

export interface Standalone {
  slug: string;
  title: string;
  description: string;
  path: string;
  contentFile: string;
  sourcePath: string;
  published: string | null;
  updated: string | null;
  verified: string | null;
  owner: string | null;
  draft: boolean;
  readingMinutes: number;
  wordCount: number;
  headings: Heading[];
  hasMermaid: boolean;
  hasCytoscape: boolean;
  hasWardley: boolean;
}

export interface Manifest {
  generatedAt: string;
  sections: Section[];
  standalone: Standalone[];
  chapterCount: number;
  sectionCount: number;
  standaloneCount: number;
}

export const manifest = manifestJson as Manifest;

export function getSection(sectionSlug: string): Section | undefined {
  return manifest.sections.find((s) => s.slug === sectionSlug);
}

export function getChapter(
  sectionSlug: string,
  chapterSlug: string,
): Chapter | undefined {
  const section = getSection(sectionSlug);
  if (!section) return undefined;
  return section.chapters.find((c) => c.slug === chapterSlug);
}

export function getStandalone(slug: string): Standalone | undefined {
  return manifest.standalone.find((s) => s.slug === slug);
}

const rawHtmlGlob = import.meta.glob('/src/content/html/*.html', {
  query: '?raw',
  import: 'default',
  eager: false,
});

function basenameOf(absolutePath: string): string {
  const idx = absolutePath.lastIndexOf('/');
  return idx >= 0 ? absolutePath.slice(idx + 1) : absolutePath;
}

type HtmlLoader = () => Promise<unknown>;

export const htmlLoaders: Record<string, HtmlLoader> = {};

for (const [absolutePath, loader] of Object.entries(rawHtmlGlob)) {
  const base = basenameOf(absolutePath);
  htmlLoaders[base] = loader as HtmlLoader;
}

export function getHtmlLoader(flatBasename: string): HtmlLoader | undefined {
  return htmlLoaders[flatBasename];
}

const htmlCache = new Map<string, string>();
const htmlInflight = new Map<string, Promise<string>>();

function resolveHtmlString(mod: unknown, contentFile: string): string {
  if (typeof mod === 'string') return mod;
  if (
    mod &&
    typeof mod === 'object' &&
    'default' in mod &&
    typeof (mod as { default: unknown }).default === 'string'
  ) {
    return (mod as { default: string }).default;
  }
  throw new Error(`HTML loader returned non-string for ${contentFile}`);
}

export function getCachedChapterHtml(contentFile: string): string | undefined {
  return htmlCache.get(contentFile);
}

export function loadChapterHtml(contentFile: string): Promise<string> {
  const cached = htmlCache.get(contentFile);
  if (cached !== undefined) return Promise.resolve(cached);

  const pending = htmlInflight.get(contentFile);
  if (pending) return pending;

  const base = contentFile.replace(/\.mdx?$/i, '.html');
  const loader = getHtmlLoader(base);
  if (!loader) {
    return Promise.reject(
      new Error(`No HTML asset for content file: ${contentFile}`),
    );
  }
  const promise = loader()
    .then((mod) => {
      const value = resolveHtmlString(mod, contentFile);
      htmlCache.set(contentFile, value);
      htmlInflight.delete(contentFile);
      return value;
    })
    .catch((err: unknown) => {
      htmlInflight.delete(contentFile);
      throw err;
    });
  htmlInflight.set(contentFile, promise);
  return promise;
}

export function prefetchChapterHtml(contentFile: string): void {
  if (htmlCache.has(contentFile) || htmlInflight.has(contentFile)) return;
  void loadChapterHtml(contentFile).catch(() => {
    // swallow prefetch failures; the real load will surface the error
  });
}

interface CitationsJson {
  generatedAt: string;
  citations: Citation[];
}

const citationsData = citationsJson as CitationsJson;

export const citationsByKey: Map<string, Citation> = new Map(
  citationsData.citations.map((c) => [c.key, c]),
);
